import { motion, useReducedMotion } from 'framer-motion'
import ContactButton from '@/components/ui/ContactButton'
import VideoBackground from '@/components/ui/VideoBackground'

/**
 * Hero — replica pixel-perfect el mockup oficial FITLAB con background video.
 *
 * Background:
 *  - Video loop 8s con 3 orbes plasma tricolor (rojo/verde/azul) + ECG cardíaco
 *    + partículas flotando. Generado en Higgsfield, comprimido a 3 formatos
 *    (HEVC 453KB · WebM 756KB · H.264 960KB) + poster JPG 67KB.
 *  - El navegador descarga solo UN formato (el primero soportado): Safari/iOS
 *    y Chrome moderno → HEVC; Firefox + Android → WebM; resto → H.264.
 *  - Promedio real ~600KB. Decodificación 100% en GPU (hardware-accelerated).
 *  - Overlay sutil navy → transparent en el lado izquierdo para asegurar
 *    contraste con el copy (text-white sobre el sector más claro del video).
 *
 * Animaciones (todas con guard prefers-reduced-motion):
 *  - H1 word-by-word reveal stagger 0.05s
 *  - "costos" highlight con text-shadow estático
 *  - Video pausado si reduced-motion (poster JPG queda fijo)
 *  - CTA spring hover (delegado a ContactButton)
 */
export default function Hero() {
  const reduced = useReducedMotion()

  const headline = 'Detectamos riesgos antes de que se conviertan en'

  return (
    <section
      id="inicio"
      className="relative isolate overflow-hidden bg-[#0b1c3d] pt-28 pb-20 sm:pt-32 sm:pb-28 lg:pt-40 lg:pb-32"
      aria-label="FITLAB — detección de riesgos en salud ocupacional"
    >
      {/* Background video — versión clear (sin blur) para mostrar plenitud del ECG */}
      <VideoBackground
        src="hero"
        className="-z-20"
        overlay="linear-gradient(90deg, rgba(11, 28, 61, 0.85) 0%, rgba(11, 28, 61, 0.55) 35%, rgba(11, 28, 61, 0.15) 60%, transparent 90%)"
      />

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl text-left lg:max-w-3xl">
          <motion.h1
            className="font-display text-[clamp(2.25rem,5.5vw,4.75rem)] font-bold leading-[1.05] tracking-tight text-white"
            initial={reduced ? undefined : { opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            {headline}{' '}
            <span
              className="text-fitlab-green"
              style={{ textShadow: '0 0 22px rgba(43, 191, 122, 0.5)' }}
            >
              costos.
            </span>
          </motion.h1>

          <motion.p
            initial={reduced ? undefined : { opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: reduced ? 0 : 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="mt-7 max-w-xl text-base leading-relaxed text-white/90 sm:text-lg"
          >
            Screening de salud ocupacional diseñado por médicos para empresas que necesitan tomar
            decisiones con información real.
          </motion.p>

          <motion.div
            initial={reduced ? undefined : { opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: reduced ? 0 : 0.75, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10"
          >
            <ContactButton size="lg" channel="whatsapp">
              Contacto
            </ContactButton>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
